import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Budget/redux/budget.selectors'
import {
  selectCreditCardIdFromRoute,
  selectCreditCardOwner,
  selectCreditCardUser,
  selectFilteredVirtualCards,
  selectVirtualCardsSnapshotPaginatedData,
} from 'src/app/BudgetOverview/redux/budgetOverview.selectors'
import BudgetOverviewBackgroundPageContent from 'src/components/BudgetOverviewBackgroundPageContent/BudgetOverviewBackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useBackgroundDispatch } from 'src/hooks'
import { useIsUserOrglessRCNOrBudgetOwner } from 'src/hooks/useOrglessUserStatus'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { accountOverviewUrl } from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import { selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed } from 'src/redux/selectors/auth.selectors'

const AccountOverview: React.FC = () => {
  const dispatch = useBackgroundDispatch()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const { data: creditCard, isLoading: isLoadingCreditCard } =
    useQueryCreditCard({
      creditCardId: creditCardId,
    })
  const {
    data: creditCardPermissions,
    isLoading: isLoadingCreditCardPermissions,
  } = useQueryCreditCardPermissions({
    creditCardId: creditCardId,
    retry: false,
  })

  const { data: parentCreditCard } = useQueryCreditCard({
    creditCardId: creditCard?.parentCreditCardId,
    enabled: creditCard?.parentCreditCardId !== undefined,
  })
  const { data: parentCreditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.parentCreditCardId,
    retry: false,
    enabled: creditCard?.parentCreditCardId !== undefined,
  })

  const creditCardOwner = useSelector(selectCreditCardOwner)
  const creditCardUser = useSelector(selectCreditCardUser)

  const virtualCardsSnapshotPaginatedData = useSelector(
    selectVirtualCardsSnapshotPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)
  const isUserOrglessRCNOrBudgetOwner = useIsUserOrglessRCNOrBudgetOwner()
  const isConfirmRCNOrBudgetModalDismissed = useSelector(
    selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed,
  )

  useEffect((): void => {
    dispatch(
      getVirtualCards({
        statuses: [VirtualCardStatus.Active],
        count: 8,
        page: 0,
        creditCardId: creditCardId,
      }),
    )
    dispatch(
      getCreditCards({
        status: CreditCardStatus.Active,
        type: CreditCardType.Delegate,
        count: 3,
        page: 0,
        sortField: 'activeUpdatedAt',
        parentCreditCard: creditCardId,
      }),
    )
  }, [creditCardId])

  useEffect(() => {
    if (isUserOrglessRCNOrBudgetOwner && !isConfirmRCNOrBudgetModalDismissed) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true))
    }
  }, [isUserOrglessRCNOrBudgetOwner])

  const isBudgetRecipient = creditCardPermissions?.canIssueVirtualCards || false

  const isLoading = (): boolean | undefined =>
    isLoadingCreditCard || isLoadingCreditCardPermissions

  // redirect to account overview if source
  useEffect(() => {
    if (creditCard?.isSource) {
      dispatch(connectedReplace(accountOverviewUrl(creditCard.id)))
    }
  }, [creditCard])

  return isLoading() ? (
    <LoadingBackgroundPage />
  ) : creditCard !== undefined && creditCardPermissions?.canRead ? (
    <BudgetOverviewBackgroundPageContent
      creditCard={creditCard}
      creditCardOwner={creditCardOwner}
      creditCardUser={creditCardUser}
      isBudgetRecipient={isBudgetRecipient}
      creditCardPermissions={creditCardPermissions}
      activeVcnCount={activeVcnCount}
      vcnCount={vcnCount}
      activeCreditCardVirtualCards={filteredVirtualCards}
      virtualCardsLoading={virtualCardsSnapshotPaginatedData.loading}
      virtualCardsComplete={virtualCardsSnapshotPaginatedData.complete}
      parentCreditCardPermissions={parentCreditCardPermissions}
      parentCreditCard={parentCreditCard}
    />
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default AccountOverview
